import { useState, useEffect, useCallback } from 'react';
import { contactsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Users, Plus, Pencil, Trash2, Building, User } from 'lucide-react';
import { toast } from 'sonner';

const emptyContact = { name: '', contact_type: 'customer', email: '', phone: '', company: '', address: '', city: '', zip_code: '', country: 'DE' };

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyContact });

  const loadContacts = useCallback(async () => {
    try {
      const res = await contactsAPI.list();
      setContacts(res.data.contacts || []);
    } catch { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await contactsAPI.update(editing, form);
        toast.success('Kontakt aktualisiert');
      } else {
        await contactsAPI.create(form);
        toast.success('Kontakt erstellt');
      }
      setShowForm(false);
      setEditing(null);
      setForm({ ...emptyContact });
      loadContacts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); }
  };

  const handleEdit = (contact) => {
    setForm({ ...contact });
    setEditing(contact.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try {
      await contactsAPI.remove(id);
      toast.success('Kontakt gelöscht');
      loadContacts();
    } catch { toast.error('Fehler beim Löschen'); }
  };

  const openNew = () => {
    setForm({ ...emptyContact });
    setEditing(null);
    setShowForm(true);
  };

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <div className="space-y-8" data-testid="contacts-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mb-2">Kontakte</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Kunden und Lieferanten verwalten</p>
        </div>
        <Button onClick={openNew} className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_15px_rgba(0,229,255,0.3)]" data-testid="add-contact-btn">
          <Plus className="w-4 h-4 mr-2" />
          <span className="hidden sm:inline">Neuer Kontakt</span>
          <span className="sm:hidden">Neu</span>
        </Button>
      </div>

      <Card className="bg-card border border-border/50" data-testid="contacts-table">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            Kontakte ({contacts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-muted-foreground">Laden...</div>
          ) : contacts.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Noch keine Kontakte</p>
              <p className="text-sm mt-1">Erstellen Sie Ihren ersten Kontakt</p>
            </div>
          ) : (
            <div className="table-responsive">
            <Table>
              <TableHeader>
                <TableRow className="border-border/30">
                  <TableHead>Name</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead className="hidden sm:table-cell">Unternehmen</TableHead>
                  <TableHead className="hidden md:table-cell">E-Mail</TableHead>
                  <TableHead className="hidden lg:table-cell">Telefon</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map((c) => (
                  <TableRow key={c.id} className="border-border/20 hover:bg-accent/50" data-testid={`contact-row-${c.id}`}>
                    <TableCell className="font-medium max-w-[120px] truncate">{c.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={c.contact_type === 'customer' ? 'text-emerald-400 border-emerald-400/30' : 'text-amber-400 border-amber-400/30'}>
                        {c.contact_type === 'customer' ? <User className="w-3 h-3 mr-1" /> : <Building className="w-3 h-3 mr-1" />}
                        {c.contact_type === 'customer' ? 'Kunde' : 'Lieferant'}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">{c.company || '-'}</TableCell>
                    <TableCell className="text-sm hidden md:table-cell">{c.email || '-'}</TableCell>
                    <TableCell className="font-mono-data text-sm hidden lg:table-cell">{c.phone || '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(c)} data-testid={`edit-contact-${c.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} className="text-destructive" data-testid={`delete-contact-${c.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      {showForm && (
        <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-card border-border/50 max-w-lg w-[95vw] sm:w-auto" data-testid="contact-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg sm:text-xl">{editing ? 'Kontakt bearbeiten' : 'Neuer Kontakt'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={form.name} onChange={update('name')} placeholder="Max Mustermann" className="bg-background h-10" required data-testid="contact-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Typ</Label>
                <Select value={form.contact_type} onValueChange={(v) => setForm({ ...form, contact_type: v })}>
                  <SelectTrigger className="bg-background h-10" data-testid="contact-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">Kunde</SelectItem>
                    <SelectItem value="vendor">Lieferant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Unternehmen</Label>
              <Input value={form.company} onChange={update('company')} placeholder="Firma GmbH" className="bg-background h-10" data-testid="contact-company-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>E-Mail</Label>
                <Input type="email" value={form.email} onChange={update('email')} placeholder="name@firma.de" className="bg-background h-10" data-testid="contact-email-input" />
              </div>
              <div className="space-y-2">
                <Label>Telefon</Label>
                <Input value={form.phone} onChange={update('phone')} placeholder="+49 123 456789" className="bg-background h-10" data-testid="contact-phone-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input value={form.address} onChange={update('address')} placeholder="Musterstraße 1" className="bg-background h-10" data-testid="contact-address-input" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>PLZ</Label>
                <Input value={form.zip_code} onChange={update('zip_code')} placeholder="12345" className="bg-background h-10" data-testid="contact-zip-input" />
              </div>
              <div className="space-y-2">
                <Label>Ort</Label>
                <Input value={form.city} onChange={update('city')} placeholder="Berlin" className="bg-background h-10" data-testid="contact-city-input" />
              </div>
              <div className="space-y-2">
                <Label>Land</Label>
                <Input value={form.country} onChange={update('country')} className="bg-background h-10" data-testid="contact-country-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Abbrechen</Button>
              <Button type="submit" className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-contact-btn">
                {editing ? 'Aktualisieren' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      )}
    </div>
  );
}
